import * as React from 'react';
import { Line } from '@visx/shape';
import { useChart } from '../xychart/providers/chart-data-provider';
import { useTooltip } from '../xychart/providers/tooltip-provider';
import getScaleBandwidth from '../xychart/utils/get-scale-bandwidth';

// A utility function to retrieve a value for an axis and return the scaled version.
// @ts-ignore
const compose = (scale, accessor) => (d) => scale(accessor(d));

type TooltipMarkerProps<Datum> = {
  data: Datum[];
  accessors: {
    xAccessor: (d: Datum) => string;
    yAccessor: (d: Datum) => [min: number, max: number] | number | null;
  };
  ariaLabel: string;
};

export function TooltipMarker<Datum>({
  data,
  accessors,
  ariaLabel,
}: TooltipMarkerProps<Datum>) {
  const { xScale, yMax } = useChart();
  const tooltip = useTooltip();

  const xPoint = compose(xScale, accessors.xAccessor);

  const barX = xPoint(data[0]);
  const barWidth = getScaleBandwidth(xScale);
  const barCenterPoint = barX + barWidth / 2;

  const showTooltip = () => {
    tooltip.showTooltip({
      tooltipData: data,
      tooltipLeft: barCenterPoint,
      tooltipTop: yMax,
    });
  };

  const hideTooltip = () => {
    tooltip.hideTooltip();
  };

  return (
    <Line
      aria-label={ariaLabel}
      tabIndex={0}
      x1={barCenterPoint}
      y1={0}
      x2={barCenterPoint}
      y2={yMax}
      stroke="transparent"
      // Makes the mouse hover area larger.
      strokeWidth={30}
      onFocus={showTooltip}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onBlur={hideTooltip}
      style={{
        outline: 'none',
      }}
    />
  );
}
